import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGripVertical, faPencil, faXmark } from "@fortawesome/free-solid-svg-icons";
import {
  putCoursePlanOrder,
  type CoursePlanItem,
} from "../auth/api-client";
import { LessonPreviewTile } from "./LessonPreviewTile";
import { QuizPreviewTile } from "./QuizPreviewTile";
import { SequenceInsertSlot } from "./SequenceInsertSlot";
import { reorderLessonList } from "./TemplateLessonSequenceRail";
import { insertAfterPlanItemIdForGap } from "./quizPlanInsert";

function planItemIds(items: readonly CoursePlanItem[]): string[] {
  return items.map((i) => i.planItemId);
}

function ordersMatch(a: readonly CoursePlanItem[], b: readonly CoursePlanItem[]): boolean {
  const aIds = planItemIds(a);
  const bIds = planItemIds(b);
  return aIds.length === bIds.length && aIds.every((id, i) => id === bIds[i]);
}

export interface CoursePlanSequenceRailProps {
  readonly courseId: string;
  readonly items: readonly CoursePlanItem[];
  readonly archived: boolean;
  readonly loading: boolean;
  readonly bearer: string | null;
  readonly onOrderSaved: () => Promise<void>;
  readonly onError: (message: string | null) => void;
  readonly onRemoveLesson?: (lessonId: string, title: string) => void;
  readonly onRemoveQuiz?: (quizId: string, title: string) => void;
  readonly onInsert?: (insertAfterPlanItemId: string | null | undefined) => void;
  readonly requireChangelog?: boolean;
  readonly onPendingOrderSave?: (nextOrder: readonly CoursePlanItem[]) => void;
  readonly resetOrderSignal?: number;
}

type SaveStatus = "idle" | "saving" | "saved" | "error";

export function CoursePlanSequenceRail({
  courseId,
  items,
  archived,
  loading,
  bearer,
  onOrderSaved,
  onError,
  onRemoveLesson,
  onRemoveQuiz,
  onInsert,
  requireChangelog = false,
  onPendingOrderSave,
  resetOrderSignal = 0,
}: CoursePlanSequenceRailProps) {
  const [orderedItems, setOrderedItems] = useState<CoursePlanItem[]>([]);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const [dragFromIndex, setDragFromIndex] = useState<number | null>(null);
  const [insertBeforeIndex, setInsertBeforeIndex] = useState<number | null>(null);
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const managePath = `/teacher/courses/${encodeURIComponent(courseId)}/manage`;

  useEffect(() => {
    setOrderedItems([...items]);
  }, [items, resetOrderSignal]);

  const persistOrder = useCallback(
    async (nextOrder: CoursePlanItem[]) => {
      if (!bearer || ordersMatch(nextOrder, items)) return;
      if (requireChangelog && onPendingOrderSave) {
        onPendingOrderSave(nextOrder);
        return;
      }
      setSaveStatus("saving");
      onError(null);
      try {
        await putCoursePlanOrder(bearer, courseId, planItemIds(nextOrder));
        await onOrderSaved();
        setSaveStatus("saved");
        if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
        savedTimerRef.current = setTimeout(() => setSaveStatus("idle"), 1800);
      } catch (err) {
        setSaveStatus("error");
        onError(err instanceof Error ? err.message : "Save order failed");
        setOrderedItems([...items]);
      }
    },
    [bearer, courseId, items, onError, onOrderSaved, onPendingOrderSave, requireChangelog]
  );

  const applyInsert = useCallback(
    (fromIndex: number, beforeIndex: number) => {
      const next = reorderLessonList(orderedItems, fromIndex, beforeIndex);
      setOrderedItems(next);
      if (!ordersMatch(next, items)) void persistOrder(next);
    },
    [items, orderedItems, persistOrder]
  );

  const renderGap = (insertBeforeIndexValue: number, insertLabel: string) => (
    <SequenceInsertSlot
      key={`gap-${insertBeforeIndexValue}`}
      disabled={archived || !onInsert}
      label={insertLabel}
      onInsert={
        onInsert
          ? () => onInsert(insertAfterPlanItemIdForGap(orderedItems, insertBeforeIndexValue))
          : undefined
      }
      isDragging={dragFromIndex !== null}
      drag={
        !archived && saveStatus !== "saving"
          ? {
              insertBeforeIndex: insertBeforeIndexValue,
              isActive: insertBeforeIndex === insertBeforeIndexValue,
              onDragOverGap: setInsertBeforeIndex,
              onDropGap: (before) => {
                if (dragFromIndex !== null) applyInsert(dragFromIndex, before);
                setDragFromIndex(null);
                setInsertBeforeIndex(null);
              },
            }
          : undefined
      }
    />
  );

  if (loading) return <p className="muted teacher-sequence-empty">Loading sequence…</p>;

  if (orderedItems.length === 0) {
    return (
      <div className="teacher-sequence-board">
        <div className="teacher-sequence-empty teacher-sequence-empty--hero">
          <p className="teacher-sequence-empty-title">Your course plan starts here</p>
          <p className="muted">Add lessons or quizzes, then drag to shape the journey.</p>
        </div>
        {!archived && onInsert ? <ol className="teacher-sequence-list">{renderGap(0, "Add item")}</ol> : null}
      </div>
    );
  }

  const reorderEnabled = !archived && saveStatus !== "saving";
  const isDragging = dragFromIndex !== null;

  return (
    <div className="teacher-sequence-board">
      {!archived ? (
        <div className="teacher-sequence-board-toolbar">
          <p className="teacher-sequence-drag-hint">
            Grab a card and drop it on a glowing slot. Order saves automatically. Hover an icon to preview.
          </p>
          {saveStatus === "saving" ? (
            <span className="teacher-sequence-save-pill is-saving" role="status">
              Saving order…
            </span>
          ) : saveStatus === "saved" ? (
            <span className="teacher-sequence-save-pill is-saved" role="status">
              Order saved
            </span>
          ) : saveStatus === "error" ? (
            <span className="teacher-sequence-save-pill is-error" role="status">
              Couldn&apos;t save. Try dragging again
            </span>
          ) : null}
        </div>
      ) : null}
      <ol
        className={`teacher-sequence-list${isDragging ? " is-dragging" : ""}`}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
            setInsertBeforeIndex(null);
          }
        }}
      >
        {renderGap(0, "Add at start")}
        {orderedItems.map((item, index) => {
          const title =
            item.itemKind === "lesson" ? item.lesson.title : item.quiz.title;
          const editUrl =
            item.itemKind === "quiz"
              ? `/teacher/courses/${encodeURIComponent(courseId)}/quizzes/${encodeURIComponent(item.quiz.id)}`
              : null;
          return (
            <Fragment key={item.planItemId}>
              <li
                className={`teacher-sequence-card teacher-sequence-card--plan${dragFromIndex === index ? " is-source" : ""}${
                  isDragging && dragFromIndex !== index ? " is-idle-during-drag" : ""
                }`}
              >
                {!archived ? (
                  <div
                    className="teacher-sequence-card-grip"
                    draggable={reorderEnabled}
                    aria-label={`Drag to reorder ${title}`}
                    title="Drag to reorder"
                    onDragStart={(e) => {
                      setDragFromIndex(index);
                      e.dataTransfer.effectAllowed = "move";
                    }}
                    onDragEnd={() => {
                      setDragFromIndex(null);
                      setInsertBeforeIndex(null);
                    }}
                  >
                    <FontAwesomeIcon icon={faGripVertical} fixedWidth />
                  </div>
                ) : (
                  <span className="teacher-sequence-card-grip-spacer" aria-hidden />
                )}
                <span className="teacher-sequence-card-icon">
                  {item.itemKind === "lesson" ? (
                    <LessonPreviewTile
                      lessonId={item.lesson.id}
                      lessonTitle={item.lesson.title}
                      courseId={courseId}
                      iconName={item.lesson.iconName}
                      iconBgColor={item.lesson.iconBgColor}
                      iconFgColor={item.lesson.iconFgColor}
                      workspaceMode={item.lesson.workspaceMode}
                      returnTo={managePath}
                      size="sm"
                    />
                  ) : (
                    <QuizPreviewTile
                      quizId={item.quiz.id}
                      quizTitle={item.quiz.title}
                      courseId={courseId}
                      returnTo={managePath}
                      iconName={item.quiz.iconName}
                      iconBgColor={item.quiz.iconBgColor}
                      iconFgColor={item.quiz.iconFgColor}
                      size="sm"
                    />
                  )}
                </span>
                <div className="teacher-sequence-card-body">
                  <span className="teacher-sequence-kind">{item.itemKind === "lesson" ? "Lesson" : "Quiz"}</span>
                  <span className="teacher-sequence-card-title">{title}</span>
                </div>
                <div className="teacher-sequence-card-actions">
                  {item.itemKind === "quiz" && editUrl ? (
                    <Link
                      className="teacher-sequence-card-edit"
                      to={editUrl}
                      title="Edit quiz"
                      aria-label={`Edit ${title}`}
                    >
                      <FontAwesomeIcon icon={faPencil} fixedWidth />
                    </Link>
                  ) : null}
                </div>
                {!archived ? (
                  item.itemKind === "lesson" && onRemoveLesson ? (
                    <button
                      type="button"
                      className="teacher-sequence-card-remove"
                      title="Remove from template"
                      aria-label={`Remove ${title} from sequence`}
                      onClick={() => onRemoveLesson(item.lesson.id, item.lesson.title)}
                    >
                      <FontAwesomeIcon icon={faXmark} fixedWidth />
                    </button>
                  ) : item.itemKind === "quiz" && onRemoveQuiz ? (
                    <button
                      type="button"
                      className="teacher-sequence-card-remove"
                      title="Delete quiz"
                      aria-label={`Delete ${title}`}
                      onClick={() => onRemoveQuiz(item.quiz.id, item.quiz.title)}
                    >
                      <FontAwesomeIcon icon={faXmark} fixedWidth />
                    </button>
                  ) : (
                    <span className="teacher-sequence-card-remove-spacer" aria-hidden />
                  )
                ) : null}
              </li>
              {renderGap(index + 1, "Add here")}
            </Fragment>
          );
        })}
      </ol>
    </div>
  );
}
